import {
    BorderRadiusMedium,
    BorderRadiusSmall,
    ColorClubMatas5,
    ColorNordicGrey4,
    Spacing16,
    Spacing4,
    Spacing8
} from 'autogen/design-tokens/tokens';
import type { BasketSampleLineViewModel } from 'autogen/swagger/all';
import Flex from 'components/Layout/components/Flex';
import Card from 'components/generic/card';
import ProductImage from 'components/generic/productImage';
import styled from 'styled-components';
import { Typography } from 'components/generic/typography';
import { hideOnDesktop, hideOnMobile } from 'components/pages/Checkout/components/Delivery/shared/styles';
import { breakpoint, sizes } from 'variables';
import { useTranslation } from 'react-i18next';
import { Free } from 'autogen/translation-keys/trans-website-basket';
import { SampleLineAchievementText } from 'autogen/translation-keys/trans-website-basket-samples';
import PriceWithDiscount from 'components/generic/productPrice/components/PriceWithDiscount';
import { Button } from 'components/Button';
import { useRemoveSampleFromBasketMutation } from 'autogen/swagger/all/hooks';
import { Delete, Edit } from 'autogen/translation-keys/trans-website-shared';
import { openSamplesModal } from 'components/samples/components/samplesPicker';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { memo } from 'react';
import { updateBasket } from '../shared/basket';
import { ProductImageContainer } from 'components/generic/productImage/shared/styles';
import {
    SelectedSampleName,
    SelectedSampleDescription
} from 'autogen/translation-keys/trans-website-basket-samples-singlesample';

/* #region styles */
const StyledCard = styled(Card)`
    border-radius: 0;
`;

const Grid = styled.div`
    display: grid;
    gap: ${Spacing16};
    grid-template-columns: 96px 1fr;


    ${breakpoint.up(sizes.lg)} {
        align-items: center;
        grid-template-columns: 96px 1fr;
    }
`;

const ProductActions = styled(Flex.Column)`
    grid-column: 1/-1;

    ${breakpoint.up(sizes.lg)} {
        grid-column: auto;
    }
`;

const StyledButton = styled(Button)`
    background-color: ${ColorNordicGrey4};
`;

const PriceContainer = styled(Flex.Column)`
    text-align: right;
`;

const StyledProductImage = styled(ProductImage)`
    width: 96px;
    height: 96px;
    background-color: ${ColorClubMatas5};
    border-radius: ${BorderRadiusMedium};

    & img {
        object-fit: cover;
        width: 100%;
        height: 100%
    }
`;
/* #endregion */

const BasketSampleItem = ({ model }: { model: BasketSampleLineViewModel }) => {
    const { image, productName, productId, isReadOnly, isSurpriseMeSample } = model;
    const { t } = useTranslation();

    const { mutateAsync: removeSample } = useRemoveSampleFromBasketMutation({
        onSuccess: () => {
            updateBasket();
            UAEventTracking({ eventCategory: 'giftLine', eventAction: 'remove', eventLabel: 'sample' });
        }
    });

    if (isSurpriseMeSample) {
        return (
            <Card size={null}>
                <Card.Content>
                    <Flex.Column>
                        <Grid>
                            <StyledProductImage
                                width={96}
                                image={{ ...image, 'aria-label': productName }}
                                transformType="SquareScale"
                            />

                            <Flex
                                direction={{ xs: 'column', lg: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'start', lg: 'center' }}
                            >
                                <Flex.Column gap={{ xs: Spacing4, lg: '0' }}>
                                    <Typography.Headline size="M" as="span">
                                        {t(SelectedSampleName)}
                                    </Typography.Headline>

                                    <Typography.Body as="span" size="M">
                                        {t(SelectedSampleDescription)}
                                    </Typography.Body>
                                </Flex.Column>

                                <Flex gap={Spacing8} direction={{ xs: 'row', lg: 'column' }} alignItems="center">
                                    <PriceWithDiscount size={14} price={t(Free)} priceBefore="" />
                                    {!isReadOnly && (
                                        <StyledButton
                                            variant="secondary"
                                            onClick={() => removeSample({ params: { productId } })}
                                        >
                                            {t(Delete)}
                                        </StyledButton>
                                    )}
                                </Flex>
                            </Flex>
                        </Grid>
                    </Flex.Column>
                </Card.Content>
            </Card>
        );
    }

    return (
        <StyledCard size={null}>
            <Card.Content>
                <Flex.Column>
                    <Grid>
                        <ProductImageContainer>
                            <ProductImage
                                width={87}
                                image={{ ...image, 'aria-label': productName }}
                                transformType="SquareScale"
                            />
                        </ProductImageContainer>

                        {/* mobile price */}
                        <Flex.Column justifyContent="space-between" css={hideOnDesktop}>
                            <Flex.Column gap={Spacing8}>
                                <Typography.Body as="span" size="S" strong>
                                    {t(SampleLineAchievementText)}
                                </Typography.Body>
                                <Typography.Body as="span">{productName}</Typography.Body>
                            </Flex.Column>

                            <PriceContainer gap={Spacing8} alignItems="end">
                                <PriceWithDiscount size={14} price={t(Free)} priceBefore="" />
                            </PriceContainer>
                        </Flex.Column>

                        <ProductActions justifyContent="space-between">
                            {/* desktop price */}
                            <Flex.Row justifyContent="space-between" css={hideOnMobile}>
                                <Typography.Body as="span">{productName}</Typography.Body>

                                <PriceContainer gap={Spacing8} alignItems="end">
                                    <Typography.Body as="span" size="S" strong>
                                        {t(SampleLineAchievementText)}
                                    </Typography.Body>
                                    <PriceWithDiscount size={14} price={t(Free)} priceBefore="" />
                                </PriceContainer>
                            </Flex.Row>

                            {!isReadOnly && (
                                <Flex.Row gap={Spacing8} justifyContent="end">
                                    <StyledButton
                                        variant="secondary"
                                        onClick={() => {
                                            openSamplesModal(t(Edit));
                                            UAEventTracking({
                                                eventCategory: 'giftLine',
                                                eventAction: 'edit',
                                                eventLabel: 'sample'
                                            });
                                        }}
                                    >
                                        {t(Edit)}
                                    </StyledButton>
                                    <StyledButton
                                        variant="secondary"
                                        onClick={() => removeSample({ params: { productId } })}
                                    >
                                        {t(Delete)}
                                    </StyledButton>
                                </Flex.Row>
                            )}
                        </ProductActions>
                    </Grid>
                </Flex.Column>
            </Card.Content>
        </StyledCard>
    );
};

export default memo(BasketSampleItem);
